import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import ContentLayout from '@cloudscape-design/components/content-layout';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import Header from '@cloudscape-design/components/header';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import Popover from '@cloudscape-design/components/popover';
import type { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import type { TableProps } from '@cloudscape-design/components/table';
import type { AddonConfig } from '@etiquetachef/types';
import {
  subscribeAddonConfigs,
  type AddonConfigListResult,
} from '@/lib/firestoreTenantDetail';
import { FilterTable } from '@/components/FilterTable';
import {
  addonFilteringOptions,
  countriesByContinent,
  toAddonRow,
  type AddonLocale,
  type AddonRow,
} from '@/features/billing/addonRow';
import { AddonEditorDialog } from '@/features/billing/AddonEditorDialog';
import { DisableAddonDialog } from '@/features/billing/DisableAddonDialog';
import { EnableAddonDialog } from '@/features/billing/EnableAddonDialog';

/**
 * Tela `/billing/addons` do admin: o catálogo de add-ons de
 * `/config/addons/items/*` na tabela padrão com filtros (`FilterTable`).
 *
 * Origem: Sprint 35 PR G1 — commit 1bc01f1b9, 2026-05-25 23:58 +0200 (sem
 * issue).
 *
 * Por quê: o catálogo importado (#679 F3) passou de 20 módulos, e os cards
 * não deixavam achar um módulo nem ver o que está pausado. A tabela filtra
 * por nome, SKU, "pode ser vendido", país e motivo, no cliente. A lista inteira
 * já chega por `subscribeAddonConfigs`, então não há callable nem índice.
 *
 * Contrato: lê `/config/addons/items/*` (listener em tempo real). As escritas
 * vão pelos diálogos: `createAddon`/`updateAddon` (`AddonEditorDialog`),
 * `disableAddon` (`DisableAddonDialog`) e `enableAddon`
 * (`EnableAddonDialog`). O cabeçalho leva à importação
 * (`/billing/addons/import`).
 *
 * Usado por: `router.tsx` (rota `billing/addons`, admin, só super-admin).
 *
 * Arquitetura: "Pode ser vendido" é a leitura de `disabled_at` (#679 D20):
 * pausa reversível, não descontinuação. Vendável aqui ainda não é vendável no
 * CRM, porque a Contratação também exige piso em `crm_appConfig/pricingFloors`.
 *
 * Dicas: a ação da linha acompanha o estado. Pausar só aparece quando o
 * módulo está à venda; retomar, só quando está pausado (teste em
 * `AddonsPage.test.tsx`). Spec: `docs/screens/billing-addons.md`.
 *
 * Issues: #679 (venda assistida, D20), #710 (catálogo e venda pausada), #907
 * (tabela com filtros), #954 (países da Play, menu de ações).
 *
 * Histórico:
 *  - 2026-06-08 21:19 +0200 — (39-E) migrada para Cloudscape.
 *  - 2026-09-24 13:31 +0200 — (#710, #679 F3) importar catálogo, retomar venda, "Pode ser vendido".
 *  - 2026-10-09 16:17 +0200 — (#907) cards trocados pela tabela padrão com filtros.
 *  - 2026-10-09 16:20 +0200 — (#907, auditoria) país por opção no filtro; ações com rótulo por linha.
 *  - 2026-10-13 08:47 +0200 — (#954) coluna de países mostra a contagem (popover por
 *    continente); ações num menu de três pontinhos.
 *  - 2026-10-13 09:27 +0200 — (#954) `["*"]` aparece como "Todos os países".
 */
export function AddonsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [state, setState] = useState<AddonConfigListResult>({ kind: 'LOADING' });
  const [editing, setEditing] = useState<AddonConfig | null>(null);
  const [creating, setCreating] = useState(false);
  const [disabling, setDisabling] = useState<AddonConfig | null>(null);
  const [enabling, setEnabling] = useState<AddonConfig | null>(null);

  useEffect(() => {
    setState({ kind: 'LOADING' });
    return subscribeAddonConfigs(setState);
  }, []);

  const lang = pickLocale(i18n.language);

  const rows = useMemo(
    () => (state.kind === 'READY' ? state.addons.map((a) => toAddonRow(a, lang, t)) : []),
    [state, lang, t],
  );

  const filteringOptions = useMemo(() => addonFilteringOptions(rows), [rows]);

  const columnDefinitions = useMemo<TableProps.ColumnDefinition<AddonRow>[]>(
    () => [
      {
        id: 'name',
        header: t('billing.addons.columns.name'),
        sortingField: 'name',
        cell: (r): React.ReactNode => (
          <div>
            {r.name}
            <Box fontSize="body-s" color="text-status-inactive" fontWeight="normal">
              <CopyToClipboard
                variant="inline"
                textToCopy={r.sku}
                textToDisplay={<span style={{ fontFamily: 'monospace' }}>{r.sku}</span>}
                copyButtonAriaLabel={t('common.copy')}
                copySuccessText={t('common.copied')}
                copyErrorText={t('common.copyError')}
              />
            </Box>
          </div>
        ),
      },
      {
        id: 'description',
        header: t('billing.addons.columns.description'),
        sortingField: 'description',
        cell: (r): React.ReactNode => r.description,
      },
      {
        id: 'sellable',
        header: t('billing.addons.sellable'),
        sortingField: 'sellable',
        cell: (r): React.ReactNode => (
          <StatusIndicator type={r.isSellable ? 'success' : 'stopped'}>{r.sellable}</StatusIndicator>
        ),
      },
      {
        id: 'reason',
        header: t('billing.addons.pausedReason'),
        sortingField: 'reason',
        cell: (r): React.ReactNode => r.reason || '—',
      },
      {
        id: 'countries',
        header: t('billing.addons.availableCountries'),
        // #954: a célula mostra a contagem, não a lista — com os 172 países da
        // Play a lista não cabia. O popover resume por continente; o filtro
        // `Disponível em : BR` continua lendo a string `countries`.
        sortingField: 'countryCount',
        cell: (r): React.ReactNode =>
          r.allCountries ? (
            // `["*"]` (#954): todos os países, inclusive os que nenhuma lista tem.
            t('billing.addons.allCountries')
          ) : r.countryCount === 0 ? (
            t('billing.addons.noCountries')
          ) : (
            <Popover
              dismissButton={false}
              size="medium"
              header={t('billing.addons.countryListTitle')}
              content={
                <KeyValuePairs
                  columns={1}
                  items={countriesByContinent(r.addon.enabled_countries).map((c) => ({
                    label:
                      c.key === 'others'
                        ? t('billing.addons.dialog.otherCountries')
                        : t(`admin.countries.catalog.continents.${c.key}`),
                    value: `${String(c.selected)}/${String(c.total)}`,
                  }))}
                />
              }
            >
              {t('billing.addons.countryCount', { count: r.countryCount })}
            </Popover>
          ),
      },
      {
        id: 'actions',
        header: t('billing.addons.columns.actions'),
        // Auditoria 2026-10-09 16:20 +0200 (#907): o rótulo acessível leva o SKU,
        // senão o leitor de tela lia N botões iguais, sem dizer a linha. Desde a
        // #954 o SKU vai no rótulo do menu (`actionsMenu`).
        minWidth: 72,
        // #954 (owner, 2026-10-13): menu de três pontinhos no lugar dos links
        // soltos, como em `ApiKeysPage`. O item de venda acompanha o estado —
        // pausar só à venda, retomar só pausado (#679 D20).
        cell: (r): React.ReactNode => (
          <ButtonDropdown
            variant="icon"
            expandToViewport
            ariaLabel={t('billing.addons.actionsMenu', { sku: r.sku })}
            items={[
              { id: 'edit', text: t('billing.addons.action.edit') },
              r.isSellable
                ? { id: 'disable', text: t('billing.addons.action.disable') }
                : { id: 'enable', text: t('billing.addons.action.enable') },
            ]}
            onItemClick={({ detail }): void => {
              if (detail.id === 'edit') setEditing(r.addon);
              else if (detail.id === 'disable') setDisabling(r.addon);
              else if (detail.id === 'enable') setEnabling(r.addon);
            }}
          />
        ),
      },
    ],
    [t],
  );

  const filteringProperties = useMemo<PropertyFilterProps.FilteringProperty[]>(() => {
    const prop = (
      key: keyof AddonRow,
      label: string,
      operators: NonNullable<PropertyFilterProps.FilteringProperty['operators']>,
    ): PropertyFilterProps.FilteringProperty => ({
      key,
      propertyLabel: label,
      groupValuesLabel: label,
      operators,
    });
    return [
      prop('name', t('billing.addons.columns.name'), ['=', '!=', ':', '!:']),
      prop('sku', t('billing.addons.skuLabel'), ['=', '!=', ':', '!:']),
      prop('sellable', t('billing.addons.sellable'), ['=', '!=']),
      prop('countries', t('billing.addons.availableCountries'), [':', '!:']),
      prop('reason', t('billing.addons.pausedReason'), [':', '!:']),
    ];
  }, [t]);

  return (
    <ContentLayout>
      <SpaceBetween size="m">
        {state.kind === 'ERROR' && <Alert type="error">{t('billing.addons.errorLoad')}</Alert>}
        <FilterTable<AddonRow>
          items={rows}
          trackBy="sku"
          loading={state.kind === 'LOADING'}
          columnDefinitions={columnDefinitions}
          filteringProperties={filteringProperties}
          filteringOptions={filteringOptions}
          empty={
            <Box textAlign="center" color="text-status-inactive" padding="l">
              {t('billing.addons.empty')}
            </Box>
          }
          header={
            <Header
              variant="h1"
              counter={state.kind === 'READY' ? `(${String(rows.length)})` : undefined}
              description={t('billing.addons.subtitle')}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    onClick={(): void => {
                      void navigate('/billing/addons/import');
                    }}
                  >
                    {t('billing.addons.action.import')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={(): void => {
                      setCreating(true);
                    }}
                  >
                    {t('billing.addons.create')}
                  </Button>
                </SpaceBetween>
              }
            >
              {t('billing.addons.title')}
            </Header>
          }
        />
      </SpaceBetween>

      <AddonEditorDialog
        open={creating || editing !== null}
        mode={editing ? 'edit' : 'create'}
        addon={editing}
        onClose={(): void => {
          setCreating(false);
          setEditing(null);
        }}
        onCreated={(): void => {
          // Na venda assistida o preço é por cliente; o piso (sem preço de
          // lista desde a #964) mora no CRM (`/admin/pricing-floors`), não em `/config/planPrices`.
          setCreating(false);
        }}
      />

      <DisableAddonDialog
        addon={disabling}
        onClose={(): void => {
          setDisabling(null);
        }}
      />

      <EnableAddonDialog
        addon={enabling}
        onClose={(): void => {
          setEnabling(null);
        }}
      />
    </ContentLayout>
  );
}

/** Idioma do i18n → locale de nome/descrição do add-on (fallback `en`). */
function pickLocale(lang: string): AddonLocale {
  if (lang.startsWith('pt')) return 'pt-BR';
  if (lang.startsWith('es')) return 'es';
  if (lang.startsWith('it')) return 'it';
  return 'en';
}
